// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import debounce from 'lodash.debounce'
import { axiosGet } from '../../util/api'
import { Tooltip } from '../ui/tooltip'
import './prototypes/variant-c/variantC.css'

// Cap how many linked rows we render at once. The list is searchable so users
// can still find any item; beyond this count DOM perf degrades noticeably.
const LINKED_RENDER_LIMIT = 500

export interface SearchResultRow {
    campaign_id: number
    campaign_name: string
    campaign_state: number
    campaign_targeting_type: number
    ad_group_id: number
    ad_group_name: string
    ad_group_state: number
    ad_group_type: number
}

export interface PromotionAdGroupLinkerProps {
    items: any[]
    onLink: (row: SearchResultRow) => void
    onUnlink: (item: any) => void
    onClose: () => void
}

interface FiltersState {
    archivedCampaigns: boolean
    pausedCampaigns: boolean
    archivedAdGroups: boolean
    pausedAdGroups: boolean
}

const DEFAULT_FILTERS: FiltersState = {
    archivedCampaigns: false,
    pausedCampaigns: false,
    archivedAdGroups: false,
    pausedAdGroups: false,
}

function Icon({ name, size = 16 }: { name: string; size?: number }) {
    const common = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.6,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    }
    switch (name) {
        case 'plus':
            return (
                <svg {...common}>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            )
        case 'x':
            return (
                <svg {...common}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            )
        case 'search':
            return (
                <svg {...common}>
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
            )
        case 'filter':
            return (
                <svg {...common}>
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
            )
        case 'swap':
            return (
                <svg {...common}>
                    <polyline points="17 1 21 5 17 9" />
                    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                    <polyline points="7 23 3 19 7 15" />
                    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
            )
        default:
            return null
    }
}

function StatusDot({ state }: { state: number }) {
    const color = state === 1 ? '#16A34A' : state === 2 ? '#DC2626' : '#94A3B8'
    return <span className="status-dot" style={{ background: color }} />
}

function MatchPill({ type }: { type: number }) {
    // type: 1=keyword, 2=product, 3=auto
    const map: Record<number, [string, string, string]> = {
        1: ['KW', '#EFF6FF', '#1D4ED8'],
        2: ['Product', '#F3E8FF', '#6B21A8'],
        3: ['Auto', '#F0F4FF', '#3B5BDB'],
    }
    const [label, bg, fg] = map[type] ?? ['—', '#F1F5F9', '#475569']
    return (
        <span className="match-pill" style={{ background: bg, color: fg }}>
            {label}
        </span>
    )
}

function isEditableElement(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) {
        return false
    }

    return (
        target.isContentEditable ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
    )
}

function AvailableSkeletonRows() {
    return (
        <>
            {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="wb2-row wb2-row--skeleton" aria-hidden="true">
                    <span className="wb2-skel-dot" />
                    <div className="wb2-row-text">
                        <div className="wb2-skel-line wb2-skel-line--group" />
                        <div className="wb2-skel-line wb2-skel-line--camp" />
                    </div>
                    <span className="wb2-skel-pill" />
                </div>
            ))}
        </>
    )
}

function FiltersPopover({
    filters,
    setFilters,
}: {
    filters: FiltersState
    setFilters: (f: FiltersState) => void
}) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [open])

    const activeCount =
        (filters.archivedCampaigns ? 1 : 0) +
        (filters.pausedCampaigns ? 1 : 0) +
        (filters.archivedAdGroups ? 1 : 0) +
        (filters.pausedAdGroups ? 1 : 0)

    return (
        <div className="wb2-filters" ref={ref}>
            <button
                type="button"
                className={'wb2-chip' + (open ? ' wb2-chip--on' : '')}
                onClick={() => setOpen((o) => !o)}
            >
                <Icon name="filter" size={13} />
                Filters
                {activeCount > 0 && <span className="wb2-chip-count">{activeCount}</span>}
            </button>
            {open && (
                <div className="wb2-pop">
                    <div className="wb2-pop-section">
                        <div className="wb2-pop-label">Campaigns</div>
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={filters.archivedCampaigns}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        archivedCampaigns: e.target.checked,
                                    })
                                }
                            />
                            <span />
                            Show archived
                        </label>
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={filters.pausedCampaigns}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        pausedCampaigns: e.target.checked,
                                    })
                                }
                            />
                            <span />
                            Show paused
                        </label>
                    </div>
                    <div className="wb2-pop-section">
                        <div className="wb2-pop-label">Ad groups</div>
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={filters.archivedAdGroups}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        archivedAdGroups: e.target.checked,
                                    })
                                }
                            />
                            <span />
                            Show archived
                        </label>
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={filters.pausedAdGroups}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        pausedAdGroups: e.target.checked,
                                    })
                                }
                            />
                            <span />
                            Show paused
                        </label>
                    </div>
                    {activeCount > 0 && (
                        <button
                            type="button"
                            className="wb2-pop-reset"
                            onClick={() => setFilters(DEFAULT_FILTERS)}
                        >
                            Reset
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export function PromotionAdGroupLinker({
    items,
    onLink,
    onUnlink,
    onClose,
}: PromotionAdGroupLinkerProps) {
    const [query, setQuery] = useState('')
    const [advanced, setAdvanced] = useState(false)
    const [campaignQuery, setCampaignQuery] = useState('')
    const [adGroupQuery, setAdGroupQuery] = useState('')
    const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS)
    const [page, setPage] = useState(1)
    const [results, setResults] = useState<SearchResultRow[]>([])
    const [total, setTotal] = useState(0)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(false)
    const requestSeq = useRef(0)

    const fetchResults = useCallback(
        (q: string, campQ: string, groupQ: string, adv: boolean, f: FiltersState, pg: number) => {
            const seq = ++requestSeq.current
            setLoading(true)
            const params: Record<string, any> = {
                page: pg,
                paused_campaigns: f.pausedCampaigns,
                archived_campaigns: f.archivedCampaigns,
                paused_ad_groups: f.pausedAdGroups,
                archived_ad_groups: f.archivedAdGroups,
            }
            if (adv) {
                if (campQ) params.campaignQuery = campQ
                if (groupQ) params.adGroupQuery = groupQ
            } else {
                if (q) params.query = q
            }
            axiosGet('promotions/search', params)
                .then((resp: any) => {
                    if (seq !== requestSeq.current) return
                    // The backend uses a LEFT JOIN ad_groups, so a campaign
                    // with no ad groups comes back as a row with
                    // ad_group_id == null. Linking such a row produces a
                    // PromotionItem with no ad group, which can't be saved.
                    // Hide them from the picker entirely.
                    const rows = (resp.data?.data ?? []).filter(
                        (r: SearchResultRow) => r.ad_group_id != null && r.campaign_id != null,
                    )
                    setResults(rows)
                    setTotal(resp.data?.total ?? 0)
                    setLastPage(resp.data?.last_page ?? 1)
                })
                .finally(() => {
                    if (seq === requestSeq.current) setLoading(false)
                })
        },
        [],
    )

    // Debounced version for query string changes
    const debouncedFetch = useMemo(
        () =>
            debounce(
                (
                    q: string,
                    campQ: string,
                    groupQ: string,
                    adv: boolean,
                    f: FiltersState,
                    pg: number,
                ) => {
                    fetchResults(q, campQ, groupQ, adv, f, pg)
                },
                300,
            ),
        [fetchResults],
    )

    // Fetch on mount
    useEffect(() => {
        fetchResults('', '', '', false, DEFAULT_FILTERS, 1)
    }, [fetchResults])

    // Debounce query changes; reset page to 1
    useEffect(() => {
        setPage(1)
        debouncedFetch(query, campaignQuery, adGroupQuery, advanced, filters, 1)
        return () => debouncedFetch.cancel()
    }, [query, campaignQuery, adGroupQuery, advanced])

    // Immediate fetch on filter changes; reset page to 1
    useEffect(() => {
        setPage(1)
        fetchResults(query, campaignQuery, adGroupQuery, advanced, filters, 1)
    }, [filters])

    // Immediate fetch on page changes
    useEffect(() => {
        fetchResults(query, campaignQuery, adGroupQuery, advanced, filters, page)
    }, [page])

    // O(1) lookup set — recomputed only when items change
    const linkedIdSet = useMemo(
        () => new Set(items.map((i) => `${i.campaign_id}-${i.ad_group_id}`)),
        [items],
    )

    const hasActiveSearch = advanced
        ? campaignQuery.trim().length > 0 || adGroupQuery.trim().length > 0
        : query.trim().length > 0

    // Enter key → close (same as clicking Done), except while typing in search fields.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !e.isComposing) {
                if (isEditableElement(e.target)) {
                    return
                }

                e.preventDefault()
                onClose()
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    return (
        <div className="vc-scope" style={{ padding: 0, minHeight: 'unset' }}>
            <div className="wb2-shell">
                {/* Toolbar */}
                <div className="wb2-toolbar">
                    <FiltersPopover filters={filters} setFilters={setFilters} />
                    <div className="wb2-toolbar-search">
                        {advanced ? (
                            <>
                                <div className="input input--icon wb2-input">
                                    <Icon name="search" size={14} />
                                    <input
                                        placeholder="Campaign name"
                                        value={campaignQuery}
                                        onChange={(e) => setCampaignQuery(e.target.value)}
                                    />
                                </div>
                                <div className="input input--icon wb2-input">
                                    <Icon name="search" size={14} />
                                    <input
                                        placeholder="Ad group name"
                                        value={adGroupQuery}
                                        onChange={(e) => setAdGroupQuery(e.target.value)}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="input input--icon wb2-input">
                                <Icon name="search" size={14} />
                                <input
                                    placeholder="Search campaign and ad group names"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </div>
                        )}
                        <Tooltip
                            content={
                                advanced
                                    ? 'Switch to simple search'
                                    : 'Switch to advanced search (separate campaign and ad group fields)'
                            }
                            position="top"
                        >
                            <button
                                type="button"
                                className={'wb2-mode' + (advanced ? ' wb2-mode--on' : '')}
                                onClick={() => setAdvanced((a) => !a)}
                                aria-label="Toggle search mode"
                            >
                                <Icon name="swap" size={13} />
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* Two-pane split */}
                <div className="wb2-split">
                    {/* Left: Available */}
                    <div className="wb2-pane wb2-pane--lib">
                        <div className="wb2-pane-head">
                            Available <span className="wb2-count">{total}</span>
                            {loading && (
                                <span
                                    style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}
                                >
                                    Loading…
                                </span>
                            )}
                        </div>
                        <div className="wb2-pane-list">
                            {!hasActiveSearch && results.length > 0 && (
                                <div
                                    style={{
                                        padding: '6px 16px 2px',
                                        fontSize: 12,
                                        color: 'var(--muted)',
                                    }}
                                >
                                    Showing first {results.length} of {total.toLocaleString()} —
                                    search to narrow results
                                </div>
                            )}
                            {loading ? (
                                <AvailableSkeletonRows />
                            ) : (
                                results.map((r) => {
                                    const key = `${r.campaign_id}-${r.ad_group_id}`
                                    const isLinked = linkedIdSet.has(key)
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className={
                                                'wb2-row' + (isLinked ? ' wb2-row--linked' : '')
                                            }
                                            disabled={isLinked || loading}
                                            onClick={() => !isLinked && onLink(r)}
                                        >
                                            <StatusDot state={r.ad_group_state} />
                                            <div className="wb2-row-text">
                                                <div className="wb2-row-group">
                                                    {r.ad_group_name}
                                                </div>
                                                <div className="wb2-row-camp">
                                                    {r.campaign_name}
                                                </div>
                                            </div>
                                            <MatchPill type={r.ad_group_type} />
                                            <span className="wb2-trailing">
                                                {isLinked ? (
                                                    <span className="badge-linked">Linked</span>
                                                ) : (
                                                    <span className="wb2-add">
                                                        <Icon name="plus" size={12} />
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    )
                                })
                            )}
                            {results.length === 0 && !loading && (
                                <div className="empty">No matches</div>
                            )}
                        </div>
                        {lastPage > 1 && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 16px',
                                    borderTop: '1px solid var(--border)',
                                    fontSize: 13,
                                    color: 'var(--muted)',
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => p - 1)}
                                    disabled={page === 1}
                                >
                                    ← Prev
                                </button>
                                <span>
                                    Page {page} of {lastPage}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={page === lastPage}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right: Linked */}
                    <div className="wb2-pane wb2-pane--linked">
                        <div className="wb2-pane-head">
                            Linked <span className="wb2-count">{items.length}</span>
                        </div>
                        <div className="wb2-pane-list">
                            {items.slice(0, LINKED_RENDER_LIMIT).map((item) => (
                                <button
                                    key={`${item.campaign_id}-${item.ad_group_id}`}
                                    type="button"
                                    className="wb2-row wb2-row--in"
                                    onClick={() => onUnlink(item)}
                                    aria-label={`Unlink ${item.ad_group_name}`}
                                >
                                    <StatusDot state={item.ad_group_state} />
                                    <div className="wb2-row-text">
                                        <div className="wb2-row-group">{item.ad_group_name}</div>
                                        <div className="wb2-row-camp">{item.campaign_name}</div>
                                    </div>
                                    <MatchPill type={item.ad_group_type} />
                                    <span className="wb2-trailing">
                                        <span className="wb2-unlink" aria-hidden="true">
                                            <Icon name="x" size={12} />
                                        </span>
                                    </span>
                                </button>
                            ))}
                            {items.length > LINKED_RENDER_LIMIT && (
                                <div
                                    style={{
                                        padding: '10px 16px',
                                        fontSize: 12,
                                        color: 'var(--muted)',
                                        textAlign: 'center',
                                    }}
                                >
                                    +{(items.length - LINKED_RENDER_LIMIT).toLocaleString()} more
                                    linked ad groups
                                </div>
                            )}
                            {items.length === 0 && (
                                <div className="empty">
                                    <div className="wb2-empty-title">No ad groups linked yet</div>
                                    <div className="wb2-empty-sub">
                                        ← Click an ad group on the left to link it
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer — styled like the save bar */}
                <div className="flex items-center justify-between px-4 h-12 border-t border-[#DBE2E8]/80 bg-white/85 backdrop-blur-md flex-shrink-0 rounded-b-[16px]">
                    <span className="text-[13px] text-[#94A3B8]">
                        {items.length > 0
                            ? `${items.length} ad group${items.length !== 1 ? 's' : ''} linked`
                            : 'No ad groups linked yet'}
                    </span>
                    <div className="flex items-center gap-3">
                        <span className="text-[12px] text-[#B0BEC9]">↵ Enter</span>
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center gap-1.5 px-3 h-7 text-[13px] font-[500] text-white bg-[#1A6FD4] hover:bg-[#1562BE] active:bg-[#1255A8] rounded-md transition-colors"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
